import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './Users.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';

const RolesList = forwardRef(({ onSelectRole, selectedRoleId, onNew }, ref) => {
  const { t } = useTranslation('admin');
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchRoles
  }));

  useEffect(() => {
    fetchRoles();
  }, [statusFilter]);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      let url = getApiUrl('/api/roles/');
      if (statusFilter !== 'all') {
        url += `?is_active=${statusFilter === 'active'}`;
      }

      const response = await apiFetch(url);

      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      } else {
        setError(t('roles.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      setError(t('roles.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredRoles = roles.filter(role => {
    const matchesSearch = (
      role.role_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.role_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return matchesSearch;
  });

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="status-badge status-active">{t('roles.filters.status.active')}</span>
    ) : (
      <span className="status-badge status-inactive">{t('roles.filters.status.inactive')}</span>
    );
  };

  const getSystemBadge = (isSystem) => {
    if (isSystem) {
      return <span className="status-badge status-active">{t('roles.tableHeaders.system')}</span>;
    }
    return null;
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && roles.length === 0) {
    return <div className="list-container"><div className="loading">{t('roles.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('roles.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('roles.newRole')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('roles.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('roles.filters.allStatus')}</option>
            <option value="active">{t('roles.filters.status.active')}</option>
            <option value="inactive">{t('roles.filters.status.inactive')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('roles.tableHeaders.code')}</th>
              <th>{t('roles.tableHeaders.name')}</th>
              <th>{t('roles.tableHeaders.description')}</th>
              <th>{t('roles.tableHeaders.system')}</th>
              <th>{t('roles.tableHeaders.status')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredRoles.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-data">
                  {searchTerm ? t('roles.noData.searching') : t('roles.noData.empty')}
                </td>
              </tr>
            ) : (
              filteredRoles.map(role => (
                <tr
                  key={role.role_id}
                  onClick={() => onSelectRole(role.role_id)}
                  className={selectedRoleId === role.role_id ? 'selected' : ''}
                >
                  <td>
                    <span className="dept-code">{role.role_code}</span>
                  </td>
                  <td>
                    <strong>{role.role_name}</strong>
                  </td>
                  <td>
                    <span className="dept-description">{role.description || '-'}</span>
                  </td>
                  <td>
                    {getSystemBadge(role.is_system)}
                  </td>
                  <td>
                    {getStatusBadge(role.is_active)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default RolesList;
